import type { PendingTask } from "@closer/shared";
import {
  groupPendingTasks,
  PENDING_TASK_GROUP_NAMES,
  PENDING_TASK_KIND_NAMES,
  pendingTaskDueState,
  TENANT_TYPE_NAMES,
} from "@closer/shared";
import { Badge, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { TaskIcon } from "../components/icons";
import { StateView } from "../components/StateView";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { pendingTaskPath } from "../lib/contractRoutes";
import { tenantKey } from "../lib/tenantQuery";
import { resolveErrorState } from "../lib/uiState";
import { TopBarPanel } from "./TopBarPanel";

/**
 * Üst Bar Bekleyen İşlemler paneli ve rozeti (CLOS-102 `[063]`).
 *
 * ### ⚠ Panel KARAR VERMİYOR
 *
 * Kartın en sert kriteri: *"kritik onay, ret, mutabakat, inceleme veya imza işlemi
 * panelden tamamlanmaz."* Burada hiçbir aksiyon yüzeyi yok — öğeler yalnız hedefe
 * götürüyor. Karar, imza ve mutabakat komutu hedef sözleşme/değişmez versiyon sayfasında
 * kendi domain doğrulamalarıyla tamamlanır. `[059]`'un `TopBarPanel` sözleşmesi de aynı
 * sınırı taşıyor.
 *
 * ### Çalışma alanı değişince sayaç SIFIRLANIR
 *
 * Kendiliğinden: sorgu `tenantKey` ile anahtarlanıyor (`[043]` konvansiyonu), yani her
 * alanın işleri kendi cache girdisinde. Ayrı bir sıfırlama çağrısı yazılsaydı ikinci bir
 * gerçek kaynağı doğar ve biri unutulduğunda eski alanın sayacı üst barda kalırdı.
 *
 * ### Veri BUGÜN boş
 *
 * Görev projeksiyonunu workflow kartları üretiyor (CLOS-156, CLOS-181). Kabuk yine de
 * gerçek bir uçtan besleniyor: kartın istediği *"yükleniyor, hata ve erişim kaybı"*
 * durumları ancak gerçek bir istek varsa var olabilir.
 */
export function PendingTasksPanel() {
  const { activeTenant, activeTenantId } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const sorgu = useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "pending-tasks")
      : ["t", "yok", "pending-tasks"],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (await api<PendingTask[]>("/api/pending-tasks", { tenantId: activeTenantId! })).data,
  });

  const gorevler = sorgu.data ?? [];
  /**
   * ⚠ İKİ GRUP, TEK ROZET (CLOS-1243 — kanon §7).
   *
   * Rozet YALNIZ `direct` sayıyor. Havuz işi listede duruyor ama "sizden bekleniyor"
   * demiyor: `any` politikalı bir aşamada beş uygun kişinin beşine de görev satırı açılıyor
   * ve eskiden beşi de rozetinde "1" görüyordu — oysa işi yalnız birinin yapması yetiyor.
   */
  const { direct: benden, claimable: ustlenebilecegim } = groupPendingTasks(gorevler);

  /**
   * Hata sunumu ORTAK KAPIDAN geçiyor (`[067]` CLOS-106): metin seçimi, teknik bilgi
   * maskeleme ve yeniden denemenin güvenli olup olmadığı orada karara bağlanıyor.
   *
   * ⚠ Erişim kaybının METNİ yine de domainin: burada 403, "kayda erişemiyorsun" değil
   * "bu ÇALIŞMA ALANINA erişimin değişti" demek. `overrides` tam da bunun için —
   * güvenlik kapısı ortak, metin domainin. Alandan çıkarma işini `[044]` yapıyor.
   */
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        // Okuma yolu: tekrarlamak güvenli.
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: {
            title: "Bu çalışma alanına erişiminiz değişti",
            description: "Bekleyen işleriniz görüntülenemiyor.",
          },
          error: {
            title: "Bekleyen işler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  function git(gorev: PendingTask) {
    setOpen(false);
    navigate(pendingTaskPath(gorev));
  }

  /**
   * Bir grubu çizer. `basligiGoster` yalnız DİĞER grup da doluysa true: tek grup varken
   * başlık atmak, olmayan bir ayrımı varmış gibi göstermek olurdu.
   */
  function grupla(kume: PendingTask[], basligiGoster: boolean) {
    if (kume.length === 0) return null;
    const grup = kume[0]!.claim;
    return (
      <section key={grup}>
        {basligiGoster ? (
          <h3
            className="px-3 pt-2 pb-1 text-xs font-medium text-fg-soft"
            data-testid={`bekleyen-grup-${grup}`}
          >
            {PENDING_TASK_GROUP_NAMES[grup]}
          </h3>
        ) : null}
        <ul>
          {kume.map((gorev) => (
            <li key={gorev.id}>
              <button
                type="button"
                onClick={() => git(gorev)}
                className="flex w-full min-w-0 flex-col px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
              >
                <span className="truncate text-sm text-fg">{gorev.contractTitle}</span>
                <span className="text-xs text-fg-soft">
                  {PENDING_TASK_KIND_NAMES[gorev.kind]}
                  {/*
                    ⚠ GECİKMİŞ İŞ EN ÇOK GÖRÜNMESİ GEREKEN İŞ (CLOS-1101). Bu görevler
                    bir süre listeden düşüyordu; "geciktiniz" bildirimi alan kullanıcı
                    panele bakıyor ve hiçbir şey bulamıyordu.

                    ⚠ Durum METİNLE anlatılıyor, yalnız renkle değil — etiketin kendisi
                    "Gecikti" kelimesini taşıyor (`pendingTaskDueState`).
                  */}
                  {(() => {
                    const durum = pendingTaskDueState(gorev.dueAt, new Date());
                    if (!durum.label) return null;
                    return (
                      <span
                        className={durum.overdue ? "text-danger" : undefined}
                        data-testid={`bekleyen-son-tarih-${gorev.id}`}
                      >
                        {" · "}
                        {durum.label}
                      </span>
                    );
                  })()}
                </span>
                {/*
                  ⚠ TALEP EDEN PANELDE DE ÇİZİLİYOR (CLOS-1112 · R5 §12.3). Tam liste bu
                  künyeyi zaten taşıyordu, panel taşımıyordu: aynı görev iki yüzeyde iki
                  farklı bilgi setiyle görünüyor ve kullanıcı "bunu benden kim istedi?"
                  sorusunun cevabını yalnız bir yerde bulabiliyordu.

                  ⚠ Başlatan bilinmiyorsa satır BOŞ değil, HİÇ çizilmiyor — "Talep eden: —"
                  bir bilgi değil, gürültüdür.
                */}
                {gorev.requestedByName ? (
                  <span className="truncate text-xs text-fg-soft">
                    Talep eden: {gorev.requestedByName}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  const tetikleyici = (
    <button
      type="button"
      onClick={() => setOpen((onceki) => !onceki)}
      aria-haspopup="menu"
      aria-expanded={open}
      /** Sayı erişilebilir ADDA da var: rozet yalnız görsel olsaydı ekran okuyucu kaçırırdı. */
      aria-label={benden.length > 0 ? `Bekleyen İşlemler (${benden.length})` : "Bekleyen İşlemler"}
      /**
       * ⚠ Rozet artık düğmenin YANINDA değil KÖŞESİNDE. Yan yana dizildiğinde sayı
       * büyüdükçe düğme genişliyor ve üst bardaki ikonların hizası kayıyordu; onaylı
       * tasarımda ikon kutusu sabit ölçüde, sayı köşede duruyor.
       */
      className="relative flex size-11 shrink-0 items-center justify-center rounded-xl border border-border-default bg-surface text-fg-muted shadow-sm transition-colors hover:border-border-strong hover:text-fg-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <TaskIcon />
      {/* Sıfır rozet DEĞİL: "0" basmak kullanıcıya bakılacak bir şey varmış gibi görünür. */}
      {benden.length > 0 ? (
        <span className="absolute -top-1.5 -right-1.5">
          <Badge tone="warning" className="px-1.5 py-0.5 text-[11px] leading-none shadow-sm">
            <span data-testid="bekleyen-rozet">{benden.length}</span>
          </Badge>
        </span>
      ) : null}
    </button>
  );

  return (
    <TopBarPanel
      open={open}
      onClose={() => setOpen(false)}
      title="Bekleyen İşlemler"
      trigger={tetikleyici}
    >
      <div className="w-80 max-w-full">
        {activeTenant ? (
          <p className="border-b border-border-default px-3 py-2 text-xs text-fg-soft">
            {activeTenant.name} · {TENANT_TYPE_NAMES[activeTenant.type]}
          </p>
        ) : null}

        {sorgu.isPending ? (
          <div role="status" aria-label="Bekleyen işler yükleniyor" className="p-3">
            <Skeleton lines={3} />
          </div>
        ) : hataDurumu ? (
          <div className="p-3">
            <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
          </div>
        ) : gorevler.length === 0 ? (
          <div className="p-3">
            <StateView
              state={{
                kind: "error",
                title: "Bekleyen işiniz yok",
                description: "Sizden beklenen ve üstlenebileceğiniz işler burada görünür.",
                retry: false,
              }}
            />
          </div>
        ) : (
          /*
            ⚠ İKİ GRUP AYRI ÇİZİLİYOR (CLOS-1243 — kanon §7).

            Başlık yalnız GEREKTİĞİNDE görünüyor: tek grup doluysa başlık bir bilgi değil,
            gürültü olurdu. İki grup birden doluysa ayrım şart — kullanıcı hangi işin
            kendisinden beklendiğini, hangisini isterse üstlenebileceğini görmeli.
          */
          <div className="py-1">
            {grupla(benden, ustlenebilecegim.length > 0)}
            {grupla(ustlenebilecegim, benden.length > 0)}
          </div>
        )}
      </div>
    </TopBarPanel>
  );
}
